import { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDAlert from "components/MDAlert";

import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";

import { request } from "app/network";

// ── config_key 상수 ─────────────────────────────────────────────────────────
const KEYS = {
    NAME: "COMPANY_NAME",
    CEO_NAME: "COMPANY_CEO_NAME",
    ADDRESS: "COMPANY_ADDRESS",
    PHONE: "COMPANY_PHONE",
    EMAIL: "COMPANY_EMAIL",
    REG_NUMBER: "COMPANY_REG_NUMBER",
    MAIL_ORDER_NO: "COMPANY_MAIL_ORDER_NO",
};

function CompanySettingsScreen(): JSX.Element {
    const [loading, setLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const [name, setName] = useState("");
    const [ceoName, setCeoName] = useState("");
    const [address, setAddress] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [regNumber, setRegNumber] = useState("");
    const [mailOrderNo, setMailOrderNo] = useState("");

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const results = await Promise.all(
                Object.values(KEYS).map((key) =>
                    request("SystemConfig.GetConfig", { paramz: { Key: key } })
                )
            );
            const [nameRes, ceoRes, addrRes, phoneRes, emailRes, regRes, mailRes] = results;

            if (nameRes?.data) setName(nameRes.data.value || "");
            if (ceoRes?.data) setCeoName(ceoRes.data.value || "");
            if (addrRes?.data) setAddress(addrRes.data.value || "");
            if (phoneRes?.data) setPhone(phoneRes.data.value || "");
            if (emailRes?.data) setEmail(emailRes.data.value || "");
            if (regRes?.data) setRegNumber(regRes.data.value || "");
            if (mailRes?.data) setMailOrderNo(mailRes.data.value || "");
        } catch (error) {
            console.error("회사 정보 설정 조회 실패:", error);
            setErrorMsg("설정을 불러오는데 실패했습니다.");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!window.confirm("회사 정보를 저장하시겠습니까?")) return;

        setLoading(true);
        setSuccessMsg(null);
        setErrorMsg(null);

        try {
            await Promise.all([
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.NAME, Value: name, Description: "회사명" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.CEO_NAME, Value: ceoName, Description: "대표자명" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.ADDRESS, Value: address, Description: "회사 주소" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.PHONE, Value: phone, Description: "대표 전화번호" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.EMAIL, Value: email, Description: "회사 이메일 (필수)" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.REG_NUMBER, Value: regNumber, Description: "사업자등록번호" } }),
                request("SystemConfig.SetConfig", { paramz: { Key: KEYS.MAIL_ORDER_NO, Value: mailOrderNo, Description: "통신판매업신고번호" } }),
            ]);

            setSuccessMsg("회사 정보가 성공적으로 저장되었습니다.");
            setTimeout(() => setSuccessMsg(null), 3000);
        } catch (error) {
            console.error("회사 정보 설정 저장 실패:", error);
            setErrorMsg("설정 저장 중 오류가 발생했습니다.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            {/* ── 헤더 ── */}
                            <MDBox
                                mx={2} mt={-3} py={3} px={2}
                                variant="gradient" bgColor="info"
                                borderRadius="lg" coloredShadow="info"
                                display="flex" justifyContent="space-between" alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">
                                    비즈니스(회사) 정보 설정
                                </MDTypography>
                            </MDBox>

                            <MDBox p={3}>
                                {/* 알림 메시지 */}
                                {successMsg && (
                                    <MDBox mb={2}><MDAlert color="success">{successMsg}</MDAlert></MDBox>
                                )}
                                {errorMsg && (
                                    <MDBox mb={2}><MDAlert color="error">{errorMsg}</MDAlert></MDBox>
                                )}

                                {/* ── 기본 정보 ── */}
                                <MDBox mb={2}>
                                    <MDTypography variant="h6" fontWeight="medium" mb={1}>
                                        기본 정보
                                    </MDTypography>
                                </MDBox>

                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="회사명"
                                                fullWidth
                                                placeholder="예: (주)메디인스텝"
                                                value={name}
                                                onChange={(e: any) => setName(e.target.value)}
                                                helperText="앱 및 약관에 표시되는 회사명"
                                            />
                                        </MDBox>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="대표자명"
                                                fullWidth
                                                placeholder="예: 홍길동"
                                                value={ceoName}
                                                onChange={(e: any) => setCeoName(e.target.value)}
                                                helperText="회사 대표자 이름"
                                            />
                                        </MDBox>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="회사 주소"
                                                fullWidth
                                                placeholder="예: 서울특별시 강남구 ..."
                                                value={address}
                                                onChange={(e: any) => setAddress(e.target.value)}
                                                helperText="사업장 주소"
                                            />
                                        </MDBox>
                                    </Grid>
                                </Grid>

                                <Divider />

                                {/* ── 연락처 ── */}
                                <MDBox mb={2}>
                                    <MDTypography variant="h6" fontWeight="medium" mb={1}>
                                        연락처
                                    </MDTypography>
                                </MDBox>

                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="대표 전화번호"
                                                fullWidth
                                                placeholder="예: 02-0000-0000"
                                                value={phone}
                                                onChange={(e: any) => setPhone(e.target.value)}
                                                helperText="회사 대표 전화번호"
                                            />
                                        </MDBox>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="회사 이메일"
                                                fullWidth
                                                placeholder="예: info@mediinstep.com"
                                                value={email}
                                                onChange={(e: any) => setEmail(e.target.value)}
                                                helperText="공식 이메일 주소 (필수)"
                                            />
                                        </MDBox>
                                    </Grid>
                                </Grid>

                                <Divider />

                                {/* ── 사업자 정보 ── */}
                                <MDBox mb={2}>
                                    <MDTypography variant="h6" fontWeight="medium" mb={1}>
                                        사업자 정보
                                    </MDTypography>
                                </MDBox>

                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="사업자등록번호"
                                                fullWidth
                                                placeholder="예: 000-00-00000"
                                                value={regNumber}
                                                onChange={(e: any) => setRegNumber(e.target.value)}
                                                helperText="10자리 사업자등록번호"
                                            />
                                        </MDBox>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <MDBox mb={2}>
                                            <MDInput
                                                label="통신판매업신고번호"
                                                fullWidth
                                                placeholder="예: 제0000-서울-00000호"
                                                value={mailOrderNo}
                                                onChange={(e: any) => setMailOrderNo(e.target.value)}
                                                helperText="통신판매업 신고번호"
                                            />
                                        </MDBox>
                                    </Grid>
                                </Grid>

                                <Divider />

                                {/* 저장 버튼 */}
                                <MDBox display="flex" justifyContent="flex-end" mt={2} mb={1}>
                                    <MDButton
                                        variant="gradient"
                                        color="info"
                                        onClick={handleSave}
                                        disabled={loading}
                                    >
                                        {loading ? "저장 중..." : "설정 저장"}
                                    </MDButton>
                                </MDBox>
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            <Footer />
        </DashboardLayout>
    );
}

export default CompanySettingsScreen;
